/**
 * Percorso di ritorno post-login, salvato prima di avviare l'autenticazione (es. la CTA "Accedi"
 * dell'anteprima pratica quando l'utente non è loggato) e consumato una sola volta dal punto di
 * ingresso dell'app (callback SPID / login credenziali / passkey).
 *
 * Persistenza in `localStorage` (NON `sessionStorage`): il ritorno deve sopravvivere anche ai flussi
 * cross-sessione/cold-start, in particolare "registrati → attiva l'account dal DEEP-LINK email in una
 * tab/sessione nuova → login". `sessionStorage` è per-tab e si svuota all'apertura di una tab nuova,
 * perdendo il returnUrl; `localStorage` sopravvive. Il consumo resta one-shot (rimosso alla lettura)
 * e un TTL breve evita di ripristinare un ritorno rimasto lì da giorni.
 */
export const POST_LOGIN_RETURN_URL_KEY = 'spidReturnUrl';

/**
 * Durata massima (ms) di validità del returnUrl salvato. Copre con margine il flusso più lungo
 * (registrazione → email di attivazione → login) senza esporre l'utente al ripristino di un ritorno
 * "vecchissimo" salvato in una sessione passata e mai consumato. Oltre la scadenza il valore è
 * scartato al consumo (comportamento identico all'assenza del valore).
 */
const POST_LOGIN_RETURN_URL_TTL_MS = 30 * 60 * 1000;

/** Envelope persistito: il target + il timestamp di salvataggio (per il TTL). */
interface StoredReturnUrl {
  url: string;
  ts: number;
}

/**
 * Prefisso della rotta "nuova pratica" autenticata. È al contempo:
 * - l'unica destinazione whitelisted per il ritorno post-login;
 * - la rotta pubblica che un anonimo può aprire in sola lettura (vedi `isPublicPraticaNuovaUrl`).
 */
const PRATICA_NUOVA_PREFIX = '/pages/pratiche/nuova/';

/**
 * Prefissi whitelisted per il ritorno post-login. Solo path interni relativi verso la compilazione
 * autenticata sono ammessi: qualsiasi altra cosa (URL assoluti, protocol-relative `//host`, path
 * fuori whitelist) viene ignorata per prevenire open-redirect.
 */
const RETURN_URL_ALLOWED_PREFIXES = [PRATICA_NUOVA_PREFIX];

/**
 * `true` se l'URL punta alla rotta pubblica "nuova pratica" (`/pages/pratiche/nuova/{slug}/{idTp}`),
 * che un utente anonimo può aprire in sola lettura senza redirect al login. Applica la stessa
 * validazione anti open-redirect del consumo (path interno, no `//host`), così l'`AuthGuard` e la
 * whitelist del returnUrl restano allineati su un'unica sorgente. Ignora eventuali query/fragment.
 */
export function isPublicPraticaNuovaUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  if (!url.startsWith('/') || url.startsWith('//')) return false;
  return url.startsWith(PRATICA_NUOVA_PREFIX);
}

/**
 * `true` se il path è un ritorno interno valido: inizia con `/` (non `//`, che sarebbe
 * protocol-relative verso un host esterno) e con uno dei prefissi whitelisted. Cuore della
 * protezione anti open-redirect, applicato sia al salvataggio sia al consumo.
 */
function isWhitelistedReturnUrl(raw: string | null | undefined): raw is string {
  if (!raw) return false;
  if (!raw.startsWith('/') || raw.startsWith('//')) return false;
  return RETURN_URL_ALLOWED_PREFIXES.some((p) => raw.startsWith(p));
}

/**
 * Salva il percorso di ritorno post-login in `localStorage` (sopravvive a cold-start e tab nuove),
 * con il timestamp per il TTL. Best-effort: se `localStorage` non è disponibile l'operazione
 * fallisce in silenzio (il login parte comunque, senza returnUrl).
 */
export function savePostLoginReturnUrl(target: string): void {
  try {
    const envelope: StoredReturnUrl = { url: target, ts: Date.now() };
    localStorage.setItem(POST_LOGIN_RETURN_URL_KEY, JSON.stringify(envelope));
  } catch {
    // best-effort: localStorage non disponibile → nessun returnUrl, login senza ritorno
  }
}

/**
 * Legge e RIMUOVE (consuma) il percorso di ritorno post-login da `localStorage`, ritornandolo solo
 * se: è entro il TTL, è un path interno whitelisted (anti open-redirect). Altrimenti ritorna `null`.
 * Il consumo è sempre one-shot: la chiave viene rimossa anche quando il valore viene scartato
 * (scaduto / non valido / illeggibile), così un ritorno "bruciato" non resta a inquinare i login
 * successivi. Tollera il formato legacy stringa-nuda (senza envelope), trattato come sempre valido
 * rispetto al TTL.
 */
export function consumePostLoginReturnUrl(): string | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(POST_LOGIN_RETURN_URL_KEY);
    localStorage.removeItem(POST_LOGIN_RETURN_URL_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  const stored = parseStored(raw);
  if (!stored) return null;
  // TTL: scarta i ritorni troppo vecchi (salvati in una sessione passata e mai consumati).
  if (Date.now() - stored.ts > POST_LOGIN_RETURN_URL_TTL_MS) return null;

  return isWhitelistedReturnUrl(stored.url) ? stored.url : null;
}

/**
 * Interpreta il valore persistito. Formato corrente = envelope JSON `{url, ts}`. Retrocompat:
 * una stringa nuda (formato pre-envelope o scritta manualmente) è accettata con `ts=now`, così
 * non viene scartata dal TTL. Un JSON malformato / senza `url` valido → `null` (scartato al consumo).
 */
function parseStored(raw: string): StoredReturnUrl | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as StoredReturnUrl).url === 'string' &&
      typeof (parsed as StoredReturnUrl).ts === 'number'
    ) {
      return parsed as StoredReturnUrl;
    }
    // JSON valido ma non è il nostro envelope (es. una stringa JSON `"..."`): trattalo come URL nudo.
    if (typeof parsed === 'string') {
      return { url: parsed, ts: Date.now() };
    }
    return null;
  } catch {
    // Non-JSON: formato legacy stringa nuda (es. `/pages/pratiche/nuova/...`).
    return { url: raw, ts: Date.now() };
  }
}
